<template>
  <v-card>
    <div class="pa-6">
      <div class="d-flex justify-end align-center pb-6">
        <v-spacer></v-spacer>
        <v-btn color="green" dark outlined @click="exportDataToExcel">
          <v-icon left>mdi-file-excel-outline</v-icon>Xuất Excel</v-btn
        >
      </div>
      <v-card>
        <v-data-table
          item-key="id"
          :headers="computedHeaders"
          :loading="loading"
          :items="marks"
          :footer-props="{ 'items-per-page-text': 'Số học sinh mỗi trang' }"
          v-bind="this.$attrs"
          fixed-header
        >
          <template v-for="subject in this.subjectsToUse" v-slot:[`item.subject_${subject.id}`]="{ item }">
            {{ item[`subject_${subject.id}`] }}
          </template>
          <template v-slot:[`item.dob`]="{ item }">
            {{ item.dob | ddmmyyyy }}
          </template>
        </v-data-table>
      </v-card>
    </div>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import XLSX from 'xlsx-js-style'
import { ExamMark } from '@/plugins/api'

const originHeaders = [
  {
    text: 'Học sinh',
    value: 'studentName',
    align: 'left',
    sortable: false,
    width: '15%'
  },
  {
    text: 'Số báo danh',
    value: 'dob',
    align: 'left',
    sortable: false,
    width: '10%'
  }
]

export default {
  props: {
    room: Object,
    query: Object,
    exam: Object,
    subjects: Array
  },
  data() {
    return {
      loading: false,
      headers: originHeaders,
      subjectHeaders: [],
      marks: [],
      markMapping: {
        absent: 'Vắng thi',
        present: 'Có thi',
        emtpy: 'Không biên bản',
        suspended: 'VPQC'
      }
    }
  },
  computed: {
    computedHeaders() {
      return [...this.headers, ...this.subjectHeaders]
    },
    subjectsToUse() {
      return this.subjects
    }
  },
  mounted() {
    this.updateSubjectHeaders(this.room)
  },
  methods: {
    ...mapActions('examMark', ['fetchMarks', 'countMarks']),
    ...mapActions('exam', ['updateExam']),
    async refresh() {
      this.$loading.active = true
      this.updateSubjectHeaders(this.room)
      this.loading = true
      try {
        const rawMarks = await this.fetchMarks({
          ...this.query,
          _limit: -1,
          _sort: 'examCode:ASC'
        })
        this.marks = this.transformMarksData(rawMarks, this.subjectsToUse)
      } catch (error) {
        console.log(error)
        this.$alert.error('Lỗi load thông tin học sinh')
      } finally {
        this.loading = false
        this.$loading.active = false
      }
    },
    updateSubjectHeaders() {
      const subjects = this.subjects
      this.subjectHeaders = subjects.map(subject => ({
        text: subject.title,
        value: `subject_${subject.id}`,
        align: 'left',
        sortable: false,
        width: '10%'
      }))
    },
    transformMarksData(marks, subjects) {
      const studentData = {}

      marks.forEach(mark => {
        if (!studentData[mark.student.id]) {
          studentData[mark.student.id] = {
            studentName: mark.student.name,
            dob: mark.student.dob,
            examCode: mark.examCode,
            rooms: new Set(),
            ...subjects.reduce(
              (acc, subject) => ({
                ...acc,
                [`subject_${subject.id}`]: '-'
              }),
              {}
            )
          }
        }

        studentData[mark.student.id][`subject_${mark.subjectGroup.id}`] =
          mark.status === 'present' ? mark.value : this.markMapping[mark.status]

        studentData[mark.student.id].rooms.add(mark.room.name)
      })

      Object.keys(studentData).forEach(studentId => {
        studentData[studentId].rooms = Array.from(studentData[studentId].rooms).join(', ')
      })

      return Object.values(studentData)
    },
    formatDataForExport(data) {
      // Get headers dynamically based on data (including subjects)
      const headers = this.getHeaders(data)

      return data.map((item, index) => {
        let formattedItem = {}

        headers.forEach(key => {
          let value = item[key] || '' // Default to empty string if value doesn't exist

          // Add STT (Serial Number) to the first column
          if (key === 'STT') {
            value = index + 1 // STT starts from 1
            formattedItem[key] = { v: value, t: 's' }
          } else {
            // Handle normal fields
            formattedItem[key] = value !== undefined && value !== null ? value : ''
          }
        })

        return formattedItem
      })
    },
    async exportDataToExcel() {
      const marks = await ExamMark.getHeadTeacherMarkExcel({ exam: this.exam._id, room: this.room._id })
      const formattedData = this.formatDataForExport(marks)
      const worksheet = XLSX.utils.json_to_sheet([], { skipHeader: true })
      const headers = [
        ['Trường THCS Lý Thái Tổ', ''],
        [`KẾT QUẢ ${this.exam.name.toUpperCase()}`, ''],
        [`Lớp: ${this.room.title}`, ''],
        [``, '']
      ]
      const signatureRow = formattedData.length + 7

      headers.forEach((header, rowIndex) => {
        const cellAddress = `A${rowIndex + 1}`
        worksheet[cellAddress] = { v: header[0], t: 's' }

        if (rowIndex === 0 || rowIndex === 2 || rowIndex === 3) {
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 13, name: 'Times New Roman' },
            alignment: { horizontal: 'left', vertical: 'center' }
          }
        }

        if (rowIndex === 1) {
          worksheet[cellAddress].s = {
            font: { bold: true, sz: 14, name: 'Times New Roman' },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true }
          }
        }
      })
      // Set column widths for columns B and C
      worksheet['!cols'] = [
        { wch: 8 }, // Column A width
        { wch: 17 }, // Column C width
        { wch: 20 }, // Column B width
        { wch: 17 }, // Column D width
        { wch: 15 }, // Column E width
        { wch: 15 }, // Column E width
        { wch: 15 }, // Column E width
        { wch: 15 }, // Column E width
        { wch: 15 }, // Column E width
        { wch: 15 }, // Column E width
        { wch: 15 } // Column E width
      ]
      // Define common styles for headers and data cells
      const dynamicHeaders = this.getHeaders(marks)
      worksheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: dynamicHeaders.length - 1 } }, // Merge row 1
        { s: { r: 1, c: 0 }, e: { r: 1, c: dynamicHeaders.length - 1 } }, // Merge row 2
        { s: { r: 2, c: 0 }, e: { r: 2, c: dynamicHeaders.length - 1 } }, // Merge row 3
        { s: { r: 3, c: 0 }, e: { r: 3, c: dynamicHeaders.length - 1 } } // Merge row 4
      ]

      // Define styles for headers and data cells
      const headerStyle = {
        font: { bold: true, sz: 12, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
      }
      const dataStyle = {
        font: { sz: 12, name: 'Times New Roman' },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } }
      }

      // Add the actual table data starting from A5
      XLSX.utils.sheet_add_json(worksheet, formattedData, {
        origin: 'A5',
        header: dynamicHeaders,
        skipHeader: false
      })

      // Apply header and data styles
      dynamicHeaders.forEach((header, colIndex) => {
        const colLetter = String.fromCharCode(65 + colIndex) // Convert column index to letter (A, B, C, ...)
        const headerCellAddress = `${colLetter}5`

        // Apply styles to header cells
        if (worksheet[headerCellAddress]) {
          worksheet[headerCellAddress].s = headerStyle
        }

        // Apply data styles to all rows
        if (formattedData.length > 0) {
          for (let rowIndex = 6; rowIndex < formattedData.length + 6; rowIndex++) {
            const dataCellAddress = `${colLetter}${rowIndex}`

            if (worksheet[dataCellAddress]) {
              worksheet[dataCellAddress].s = dataStyle
            }
          }
        }
      })

      const endRow = signatureRow + 6
      worksheet['!ref'] = `A1:${String.fromCharCode(65 + dynamicHeaders.length - 1)}${endRow}`

      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Điểm')
      XLSX.writeFile(workbook, `diem-${this.room.title}.xlsx`)

      this.$loading.active = false
    },
    getHeaders(data) {
      // Extract the headers from the first item in the data
      const firstItem = data[0] || {}

      // Collect all fixed fields first
      const headers = ['STT', 'Mã HS', 'Học sinh', 'Ngày sinh']

      // Dynamically add subjects to the headers
      Object.keys(firstItem).forEach(key => {
        if (!headers.includes(key)) {
          headers.push(key) // This includes subject marks
        }
      })

      return headers
    }
  },
  watch: {
    query: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.refresh()
        }
      }
    }
  }
}
</script>
